import { avatarAppConfig } from "../Components/Avatar/config";

/**
 * Universal High-Fidelity Voice Synthesis Service
 * Primary Engine: Telnyx AI High-Definition TTS Playground API
 * Fallback Engines: Microsoft Azure Neural US / Sarvam AI / Web Speech API
 */
class HighDefinitionTtsService {
  constructor() {
    this.currentAudio = null;
    this.abortController = null;
    this.isSpeaking = false;
    this.isPlayingAudio = false;
    this.listeners = new Set();
    this.audioCache = new Map(); // In-memory cache for instantaneous zero-latency playback
    this.queue = []; // Sentence completion queue
    this.currentText = "";
    this.progressAnimationId = null;
  }

  getTelnyxKey() {
    return (
      process.env.REACT_APP_TELNYX_API_KEY ||
      avatarAppConfig?.telnyxAPIKey ||
      "KEY01A05C4CD8AB479C4FB09B5CFF5DF7EE_ikhvHeBBPUDr7kPqapDMMj"
    );
  }

  getAzureConfig() {
    return {
      key:
        process.env.REACT_APP_AZURE_SPEECH_KEY ||
        avatarAppConfig?.cogSvcSubKey ||
        "1IiMC3dgY83tkF1MRHtATL3L4UVxbJ5g0MT4dWXX6GZpPO9QbJv0JQQJ99CEACqBBLyXJ3w3AAAYACOGIXoL",
      region:
        process.env.REACT_APP_AZURE_SPEECH_REGION ||
        avatarAppConfig?.cogSvcRegion ||
        "southeastasia"
    };
  }

  getSarvamKey() {
    return (
      process.env.REACT_APP_SARVAM_API_KEY ||
      avatarAppConfig?.sarvamAPIKey ||
      "sk_0k69tgy2_AVfNH0yTxm9HywkCfqd3CHAk"
    );
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify(speakingState) {
    this.isSpeaking = speakingState;
    this.listeners.forEach((fn) => {
      try {
        fn(speakingState);
      } catch (_) {}
    });
  }

  cleanTextForSpeech(text) {
    if (!text) return "";
    let clean = text
      .replace(
        /[\u{1F600}-\u{1F6FF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}|\u{1F300}-\u{1F5FF}|\u{1F900}-\u{1F9FF}|\u{1F018}-\u{1F270}|\u{2388}-\u{2454}]/gu,
        ""
      )
      .replace(/[•*#_~`]/g, "")
      .replace(/https?:\/\/\S+/g, "")
      .replace(/&/g, " and ");

    // Currency and financial term phonetic normalization
    // 1. Negative amounts / credits (e.g. -$200/mo, -$200, -$ 200/mo)
    clean = clean.replace(/[-–]\s*\$(\d[\d,]*(?:\.\d+)?)\s*\/(?:mo|month)/gi, "a credit of $1 dollars per month");
    clean = clean.replace(/[-–]\s*\$(\d[\d,]*(?:\.\d+)?)/gi, "minus $1 dollars");
    // 2. Positive / addition amounts (e.g. +$60/mo, +$60)
    clean = clean.replace(/\+\s*\$(\d[\d,]*(?:\.\d+)?)\s*\/(?:mo|month)/gi, "$1 dollars per month");
    clean = clean.replace(/\+\s*\$(\d[\d,]*(?:\.\d+)?)/gi, "$1 dollars");
    // 3. General dollar amounts with per month / per year
    clean = clean.replace(/\$(\d[\d,]*(?:\.\d+)?)\s*\/(?:mo|month)/gi, "$1 dollars per month");
    clean = clean.replace(/\$(\d[\d,]*(?:\.\d+)?)\s*\/(?:yr|year)/gi, "$1 dollars per year");
    clean = clean.replace(/\$(\d[\d,]*(?:\.\d+)?)/g, "$1 dollars");
    // 4. APR and percentages
    clean = clean.replace(/(\d+(?:\.\d+)?)%\s*APR/gi, "$1 percent A-P-R");
    clean = clean.replace(/(\d+(?:\.\d+)?)%/g, "$1 percent");
    clean = clean.replace(/\/mo\b/gi, " per month");
    clean = clean.replace(/\/yr\b/gi, " per year");
    // 5. Mileage, terms & acronyms
    clean = clean.replace(/(\d+)k\s*mi(?:les?)?/gi, "$1 thousand miles");
    clean = clean.replace(/(\d[\d,]*)\s*mi(?:\b|\.)/gi, "$1 miles");
    clean = clean.replace(/(\d+)\s*yr(?:s)?\b/gi, "$1 years");
    clean = clean.replace(/\bVIN\b/g, "V-I-N");
    clean = clean.replace(/\bF&I\b/gi, "F and I");
    clean = clean.replace(/\bVSC\b/g, "Vehicle Service Contract");
    clean = clean.replace(/\bGAP\b/g, "Gap Protection");

    // 6. Deduplicate accidental double unit words (e.g. "miles miles", "mi miles", "year years", "dollars dollars")
    clean = clean.replace(/\b(?:miles?|mi)\s+(?:miles?|mi)\b/gi, "miles");
    clean = clean.replace(/\byears?\s+years?\b/gi, "years");
    clean = clean.replace(/\bdollars?\s+dollars?\b/gi, "dollars");
    clean = clean.replace(/\bmonths?\s+months?\b/gi, "months");
    clean = clean.replace(/\bpercent\s+percent\b/gi, "percent");

    // Pronunciation & phonetic name correction
    clean = clean.replace(/\bAimee\b/gi, "Amy");
    clean = clean.replace(/\bAIMEE\b/g, "Amy");
    clean = clean.replace(/\bAimme\b/gi, "Amy");
    clean = clean.replace(/\bAi me\b/gi, "Amy");

    // 7. Formatting cleanup
    clean = clean.replace(/</g, " ");
    clean = clean.replace(/>/g, " ");
    clean = clean.replace(/\n+/g, ". ");
    clean = clean.replace(/\s+/g, " ");
    return clean.trim();
  }

  getAudioContext() {
    if (typeof window === "undefined") return null;
    if (!this.audioContext || this.audioContext.state === "closed") {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.audioContext = new AudioCtx();
      }
    }
    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }

  stop() {
    this.queue = []; // Clear pending queue on explicit stop
    this.currentText = "";
    this.isPlayingAudio = false;

    if (this.progressAnimationId) {
      cancelAnimationFrame(this.progressAnimationId);
      this.progressAnimationId = null;
    }

    if (this.currentSourceNode) {
      try {
        this.currentSourceNode.onended = null;
        this.currentSourceNode.stop();
        this.currentSourceNode.disconnect();
      } catch (_) {}
      this.currentSourceNode = null;
    }

    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch (_) {}
      this.abortController = null;
    }

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.src = "";
      } catch (_) {}
      this.currentAudio = null;
    }

    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {}
    }

    this.notify(false);
  }

  async speak(rawText, options = {}) {
    const cleanText = this.cleanTextForSpeech(rawText);
    if (!cleanText) return;

    // If explicit interrupt is requested OR queue already has items, purge backlog so user doesn't experience lag!
    if (options.interrupt || this.queue.length >= 1) {
      this.stop();
    }

    // Prevent duplicate enqueue of the exact same sentence
    if (this.currentText === cleanText || this.queue.some(item => item.cleanText === cleanText)) {
      return;
    }

    // Enqueue sentence
    this.queue.push({ cleanText, rawText, options });

    if (!this.isPlayingAudio) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.queue.length === 0) {
      this.isPlayingAudio = false;
      this.currentText = "";
      this.notify(false);
      return;
    }

    const nextItem = this.queue.shift();
    const { cleanText, options } = nextItem;
    const defaultTelnyxVoice = avatarAppConfig?.telnyxVoiceName || "Telnyx.Ultra.045f0292-0731-4a4c-971d-64594fc2c35a";
    const {
      telnyxVoice = defaultTelnyxVoice,
      onStart,
      onProgress,
      onEnd,
      onError
    } = options;

    this.isPlayingAudio = true;
    this.currentText = cleanText;
    this.abortController = new AbortController();

    const handleSentenceFinished = () => {
      this.isPlayingAudio = false;
      this.currentText = "";
      if (onEnd) onEnd();
      this.processQueue();
    };

    const handleSentenceError = () => {
      this.isPlayingAudio = false;
      this.currentText = "";
      if (onError) onError();
      this.processQueue();
    };

    // 1. Primary Engine: Telnyx AI TTS Playground
    await this.speakWithTelnyx(cleanText, telnyxVoice, onStart, handleSentenceFinished, handleSentenceError, onProgress);
  }

  async preload(rawText, options = {}) {
    const cleanText = this.cleanTextForSpeech(rawText);
    if (!cleanText) return;
    const voice = options.telnyxVoice || avatarAppConfig?.telnyxVoiceName || "Telnyx.Ultra.045f0292-0731-4a4c-971d-64594fc2c35a";
    const cacheKey = `telnyx-${voice}-${cleanText}`;
    if (this.audioCache.has(cacheKey)) return;
    try {
      const apiKey = this.getTelnyxKey();
      const response = await fetch("https://api.telnyx.com/v2/text-to-speech/speech", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ text: cleanText, voice: voice })
      });
      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer();
        const ctx = this.getAudioContext();
        if (ctx) {
          const decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
          this.audioCache.set(cacheKey, { arrayBuffer, decoded, duration: decoded.duration });
        }
      }
    } catch (_) {}
  }

  async speakWithTelnyx(cleanText, voice = "Telnyx.Ultra.045f0292-0731-4a4c-971d-64594fc2c35a", onStart, onEnd, onError, onProgress) {
    try {
      const apiKey = this.getTelnyxKey();
      const cacheKey = `telnyx-${voice}-${cleanText}`;

      // Check in-memory audio cache for zero-latency instant playback
      if (this.audioCache.has(cacheKey)) {
        const cachedItem = this.audioCache.get(cacheKey);
        await this.playWebAudio(cachedItem, onStart, onEnd, onError, onProgress);
        return;
      }

      const response = await fetch("https://api.telnyx.com/v2/text-to-speech/speech", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          text: cleanText,
          voice: voice
        }),
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        throw new Error(`Telnyx TTS API HTTP ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const ctx = this.getAudioContext();
      let decoded = null;
      if (ctx) {
        try {
          decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
        } catch (_) {}
      }

      const cachePayload = {
        arrayBuffer,
        decoded,
        duration: decoded?.duration || 0
      };

      // Cache audio source
      this.audioCache.set(cacheKey, cachePayload);

      await this.playWebAudio(cachePayload, onStart, onEnd, onError, onProgress);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("Telnyx AI TTS failed, trying Azure Neural fallback:", err?.message || err);
      this.speakWithAzureFallback(cleanText, onStart, onEnd, onError, onProgress);
    }
  }

  async playWebAudio(audioPayload, onStart, onEnd, onError, onProgress) {
    try {
      const ctx = this.getAudioContext();
      if (!ctx) {
        throw new Error("AudioContext not supported");
      }

      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      let audioBuffer = audioPayload.decoded;
      if (!audioBuffer && audioPayload.arrayBuffer) {
        audioBuffer = await ctx.decodeAudioData(audioPayload.arrayBuffer.slice(0));
        audioPayload.decoded = audioBuffer;
      }

      if (!audioBuffer) {
        throw new Error("Unable to decode audio buffer");
      }

      const exactDuration = audioBuffer.duration;
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      this.currentSourceNode = source;

      const startCtxTime = ctx.currentTime;
      this.notify(true);
      if (onStart) onStart({ duration: exactDuration });

      let isFinished = false;
      const completeSession = () => {
        if (isFinished) return;
        isFinished = true;
        if (this.progressAnimationId) {
          cancelAnimationFrame(this.progressAnimationId);
          this.progressAnimationId = null;
        }
        if (onProgress) {
          onProgress({
            currentTime: exactDuration,
            duration: exactDuration,
            progress: 1.0,
            text: this.currentText
          });
        }
        this.notify(false);
        this.currentSourceNode = null;
        if (onEnd) onEnd();
      };

      source.onended = completeSession;

      const trackProgress = () => {
        if (isFinished || !this.currentSourceNode) return;
        const elapsed = ctx.currentTime - startCtxTime;
        const progress = exactDuration > 0 ? Math.min(0.999, Math.max(0, elapsed / exactDuration)) : 0;

        if (onProgress) {
          onProgress({
            currentTime: elapsed,
            duration: exactDuration,
            progress: progress,
            text: this.currentText
          });
        }

        if (elapsed >= exactDuration) {
          completeSession();
          return;
        }

        this.progressAnimationId = requestAnimationFrame(trackProgress);
      };

      source.start(0);
      this.progressAnimationId = requestAnimationFrame(trackProgress);
    } catch (err) {
      console.warn("WebAudio playback fallback to HTML5 Audio:", err);
      // Fallback to Blob URL HTML5 Audio
      if (audioPayload.arrayBuffer) {
        const blob = new Blob([audioPayload.arrayBuffer], { type: "audio/mp3" });
        const url = URL.createObjectURL(blob);
        this.playAudioSource(url, onStart, onEnd, onError, onProgress);
      } else {
        if (onError) onError(err);
      }
    }
  }

  async speakWithAzureFallback(cleanText, onStart, onEnd, onError, onProgress) {
    try {
      const { key, region } = this.getAzureConfig();
      const voice = "en-US-AvaMultilingualNeural";
      const locale = "en-US";
      const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${locale}"><voice name="${voice}">${cleanText}</voice></speak>`;

      const response = await fetch(
        `https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`,
        {
          method: "POST",
          headers: {
            "Ocp-Apim-Subscription-Key": key,
            "Content-Type": "application/ssml+xml",
            "X-Microsoft-OutputFormat": "audio-24khz-48kbitrate-mono-mp3",
            "User-Agent": "SaleSenseAI"
          },
          body: ssml,
          signal: this.abortController?.signal
        }
      );

      if (!response.ok) throw new Error(`Azure Speech API returned ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      const ctx = this.getAudioContext();
      let decoded = null;
      if (ctx) {
        try {
          decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
        } catch (_) {}
      }

      const cachePayload = {
        arrayBuffer,
        decoded,
        duration: decoded?.duration || 0
      };

      await this.playWebAudio(cachePayload, onStart, onEnd, onError, onProgress);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("Azure fallback failed, attempting Sarvam fallback:", err?.message || err);
      this.speakWithSarvamFallback(cleanText, onStart, onEnd, onError, onProgress);
    }
  }

  async speakWithSarvamFallback(cleanText, onStart, onEnd, onError, onProgress) {
    try {
      const apiKey = this.getSarvamKey();
      const textToSpeak = cleanText.length > 250 
        ? (cleanText.split(/(?<=[.?!])\s+/)[0] || cleanText.substring(0, 200))
        : cleanText;

      const response = await fetch("https://api.sarvam.ai/text-to-speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-subscription-key": apiKey
        },
        body: JSON.stringify({
          inputs: [textToSpeak],
          target_language_code: "en-IN",
          speaker: "shreya",
          model: "bulbul:v3",
          pace: 1.0
        }),
        signal: this.abortController?.signal
      });

      if (!response.ok) throw new Error("Sarvam fallback failed");

      const data = await response.json();
      const base64Audio = data?.audios?.[0];
      if (!base64Audio) throw new Error("No audio from Sarvam");

      // Decode base64 to binary ArrayBuffer for exact Web Audio hardware clock playback
      const binaryString = atob(base64Audio);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const arrayBuffer = bytes.buffer;

      const ctx = this.getAudioContext();
      let decoded = null;
      if (ctx) {
        try {
          decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
        } catch (_) {}
      }

      const cachePayload = {
        arrayBuffer,
        decoded,
        duration: decoded?.duration || 0
      };

      await this.playWebAudio(cachePayload, onStart, onEnd, onError, onProgress);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("Sarvam fallback failed, using Web Speech API as last resort:", err);
      this.fallbackSpeakWebSpeech(cleanText, onStart, onEnd, onError, onProgress);
    }
  }

  playAudioSource(audioSrc, onStart, onEnd, onError, onProgress) {
    try {
      const audio = new Audio(audioSrc);
      this.currentAudio = audio;
      let startPerfTime = 0;
      let knownDuration = 0;

      const getSafeDuration = () => {
        if (knownDuration > 0) return knownDuration;
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          knownDuration = audio.duration;
          return knownDuration;
        }
        // Approximate fallback duration based on sentence character length (~65ms/char)
        return this.currentText ? Math.max(1.5, this.currentText.length * 0.065) : 2.5;
      };

      const startProgressTracking = () => {
        if (this.progressAnimationId) cancelAnimationFrame(this.progressAnimationId);
        startPerfTime = performance.now();

        const updateFrame = () => {
          if (!this.currentAudio || this.currentAudio !== audio || audio.paused || audio.ended) {
            return;
          }

          const duration = getSafeDuration();
          let curTime = audio.currentTime;
          if (curTime === undefined || isNaN(curTime) || curTime <= 0) {
            curTime = (performance.now() - startPerfTime) / 1000;
          }

          const progress = duration > 0 ? Math.min(0.99, Math.max(0, curTime / duration)) : 0;

          if (onProgress) {
            onProgress({
              currentTime: curTime,
              duration,
              progress,
              text: this.currentText
            });
          }

          this.progressAnimationId = requestAnimationFrame(updateFrame);
        };

        this.progressAnimationId = requestAnimationFrame(updateFrame);
      };

      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          knownDuration = audio.duration;
        }
      });

      audio.addEventListener('durationchange', () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          knownDuration = audio.duration;
        }
      });

      audio.onplay = () => {
        this.notify(true);
        if (onStart) onStart({ duration: getSafeDuration() });
        startProgressTracking();
      };

      audio.onended = () => {
        if (this.progressAnimationId) {
          cancelAnimationFrame(this.progressAnimationId);
          this.progressAnimationId = null;
        }
        const finalDuration = getSafeDuration();
        if (onProgress) {
          onProgress({
            currentTime: finalDuration,
            duration: finalDuration,
            progress: 1.0,
            text: this.currentText
          });
        }
        this.notify(false);
        this.currentAudio = null;
        if (onEnd) onEnd();
      };

      audio.onerror = (e) => {
        console.warn("Audio playback error:", e);
        if (this.progressAnimationId) {
          cancelAnimationFrame(this.progressAnimationId);
          this.progressAnimationId = null;
        }
        this.notify(false);
        this.currentAudio = null;
        if (onError) onError(e);
      };

      audio.play().catch((err) => {
        console.warn("Audio playback prevented by browser autoplay policy:", err);
        if (this.progressAnimationId) {
          cancelAnimationFrame(this.progressAnimationId);
          this.progressAnimationId = null;
        }
        this.notify(false);
        if (onError) onError(err);
      });
    } catch (err) {
      if (this.progressAnimationId) {
        cancelAnimationFrame(this.progressAnimationId);
        this.progressAnimationId = null;
      }
      this.notify(false);
      if (onError) onError(err);
    }
  }

  // Fallback to browser TTS
  fallbackSpeak(text, onStart, onEnd, onError, onProgress) {
    this.speakWithTelnyx(text, "Telnyx.Ultra.045f0292-0731-4a4c-971d-64594fc2c35a", onStart, onEnd, onError, onProgress);
  }

  fallbackSpeakWebSpeech(text, onStart, onEnd, onError, onProgress) {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      this.notify(false);
      if (onError) onError();
      return;
    }

    try {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      let speechStartTime = 0;
      let progressTimer = null;
      const estimatedDurationMs = Math.max(1200, text.length * 55); // approx 55ms per char

      utterance.onstart = () => {
        this.notify(true);
        speechStartTime = Date.now();
        if (onStart) onStart({ duration: estimatedDurationMs / 1000 });

        progressTimer = setInterval(() => {
          const elapsed = Date.now() - speechStartTime;
          const progress = Math.min(0.98, elapsed / estimatedDurationMs);
          if (onProgress) {
            onProgress({
              currentTime: elapsed / 1000,
              duration: estimatedDurationMs / 1000,
              progress,
              text
            });
          }
        }, 30);
      };

      utterance.onboundary = (event) => {
        if (event.charIndex !== undefined && text.length > 0) {
          const progress = Math.min(1, (event.charIndex + (event.charLength || 4)) / text.length);
          if (onProgress) {
            onProgress({
              currentTime: (Date.now() - speechStartTime) / 1000,
              duration: estimatedDurationMs / 1000,
              progress,
              text
            });
          }
        }
      };

      utterance.onend = () => {
        if (progressTimer) clearInterval(progressTimer);
        if (onProgress) {
          onProgress({
            currentTime: estimatedDurationMs / 1000,
            duration: estimatedDurationMs / 1000,
            progress: 1.0,
            text
          });
        }
        this.notify(false);
        if (onEnd) onEnd();
      };

      utterance.onerror = (e) => {
        if (progressTimer) clearInterval(progressTimer);
        this.notify(false);
        if (onError) onError(e);
      };

      window.speechSynthesis.speak(utterance);
    } catch (_) {
      this.notify(false);
      if (onError) onError();
    }
  }
}

export const sarvamTts = new HighDefinitionTtsService();
export default sarvamTts;
